import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2'
import './Register.css';

const HEALTH_RECORDS_URL = 'https://iron.nextriking.com/api/health-records';

const Register = () => {
    const userRef = useRef();
    const [name, setUsername] = useState('');
    const [lastname, setLastname] = useState('');
    const [service_id, setService] = useState('');
    const [email, setEmail] = useState('');
    const [sessionsWeek, setSessions] = useState('');
    const [urlRoutine, setRutine] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [phone, setPhone] = useState('');
    const [birthDate, setBirthdate] = useState('');
    const [firstPayment, setFirstPayment] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [documentStatus, setDocumentStatus] = useState(null);
    const [tyc, setTyc] = useState(false);
    const [healthRecord, setHealthRecord] = useState({
        medicalConditions: '',
        injuriesRestrictions: '',
        allergies: '',
        observations: '',
    });

    const navigate = useNavigate();

    useEffect(() => {
        userRef.current.focus();
    }, []);

    const getServiceName = (id) => {
        if (id === 1) return "Sala libre";
        if (id === 2) return "Sala HIFT";
        if (id === 3) return "Personalizado";
        if (id === 4) return "Mixto";
        return "Desconocido";
    };

    const formData = {
        id: 0,
        name,
        lastname,
        email,
        urlRoutine,
        documentNumber,
        phone,
        birthDate,
        firstPayment,
        usertype: 3,
        service_id,
        sessionsWeek,
        tyc: 1
    };

    const sanitizedData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [key, value === '' ? null : value])
    );

    const handleSessionsChange = (e) => {
        const value = e.target.value;
        // Solo permite números del 1 al 7
        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 7)) {
            setSessions(value);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setLoading(true);

        if (!name || !lastname || !documentNumber || !firstPayment) {
            const message = 'Por favor, completa todos los campos obligatorios.';
            setError(message);
            await Swal.fire('Datos incompletos', message, 'warning');
            setLoading(false);
            return;
        }

        const confirmMessage = `
            <p><strong>Nombre:</strong> ${name}</p>
            <p><strong>Apellido:</strong> ${lastname}</p>
            <p><strong>Servicio:</strong> ${getServiceName(service_id)}</p>
            <p><strong>Sesiones por semana:</strong> ${sessionsWeek}</p>
            <p><strong>Email:</strong> ${email}</p>
            <p><strong>Documento:</strong> ${documentNumber}</p>
            <p><strong>Teléfono:</strong> ${phone}</p>
            <p><strong>Fecha de Nacimiento:</strong> ${birthDate}</p>
            <p><strong>Fecha de primera cuota:</strong> ${firstPayment}</p>
        `;

        // Mostrar el diálogo de confirmación
        const result = await Swal.fire({
            title: '¿Confirmar registro?',
            html: confirmMessage,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, confirmar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            let userCreated = false;
            try {
                const response = await axios.post('https://iron.nextriking.com/api/users/add', sanitizedData);

                if (response.status >= 200 && response.status < 300) {
                    userCreated = true;
                    const hasHealthInformation = Object.values(healthRecord).some(Boolean);

                    if (hasHealthInformation) {
                        let userId = response.data.body?.id ?? response.data.body?.insertId ?? response.data.id;
                        if (!userId) {
                            const userResponse = await axios.get(`https://iron.nextriking.com/api/users/email/${documentNumber}`);
                            userId = userResponse.data.body?.[0]?.id;
                        }
                        if (!userId) throw new Error('No se recibió el identificador del usuario creado.');
                        await axios.post(HEALTH_RECORDS_URL, { userId, ...healthRecord });
                    }

                    await Swal.fire({
                        title: 'Usuario registrado',
                        text: 'El usuario fue dado de alta correctamente.',
                        icon: 'success',
                        confirmButtonText: 'OK'
                    });
                    window.location.reload();
                }
            } catch (error) {
                const message = userCreated
                    ? 'El usuario fue registrado, pero no se pudo guardar la ficha de salud.'
                    : 'No se pudo registrar el usuario. Inténtalo de nuevo más tarde.';
                setError(message);
                await Swal.fire(userCreated ? 'Ficha de salud no guardada' : 'Error al registrar', message, userCreated ? 'warning' : 'error');
                if (userCreated) window.location.reload();
                console.error('Error al registrar usuario:', error);
            }
        }
        setLoading(false);
    };

    const handleDocumentChange = async (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value) && value.length <= 8) {
            setDocumentNumber(value);

            if (value.length === 8) {
                try {
                    const response = await axios.post(
                        "https://iron.nextriking.com/api/users/dni",
                        { documentNumber: value },
                        { headers: { "Content-Type": "application/json" } }
                    );
                    console.log("response:", response);
                    const exists = response.data.body.exists;
                    setDocumentStatus(exists);
                } catch (error) {
                    console.error("Error al verificar el DNI:", error);
                    setDocumentStatus(null);
                }
            } else {
                setDocumentStatus(null);
            }
        }
    };

    const handleServiceChange = (e) => {
        const selectedServiceId = Number(e.target.value);
        setService(selectedServiceId);

        if (selectedServiceId === 2) {
            setRutine('https://docs.google.com/spreadsheets/d/1cXFc8fMhAkg5goBpBKiO8BZ8G97x5EIN/edit?rtpof=true&sd=true&gid=1926737728#gid=1926737728');
        }
    };

    const handleHealthRecordChange = (event) => {
        const { name, value } = event.target;
        setHealthRecord((previous) => ({ ...previous, [name]: value }));
    };

    return (
        <main className="register-page">
            <Card className="register-card">
                <Card.Body>
                    <div>
                        <button type="button" className="btn btn-outline-secondary register-card__back" onClick={() => navigate("/dashboard")}>
                            Volver atrás
                        </button>
                    </div>
                    <div className="register-card__heading"><h1>Dar de alta usuario</h1><p>Completá los datos del alumno para crear su ficha.</p></div>
                    {error && <p className="alert alert-danger register-alert--error">{error}</p>}
                    <Form onSubmit={handleSubmit} className="register-form">
                        <Form.Group className="register-form__group" controlId="formBasicUsername">
                            <Form.Label>Nombre</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Nombre"
                                value={name}
                                ref={userRef}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="register-form__group" controlId="formBasicLastname">
                            <Form.Label>Apellido</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Apellido"
                                value={lastname}
                                onChange={(e) => setLastname(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="register-form__group" controlId="formBasicService">
                            <Form.Label>Servicio</Form.Label>
                            <Form.Select
                                value={service_id}
                                onChange={(e) => handleServiceChange(e)}
                            >
                                <option value="">Selecciona una opción</option>
                                <option value="1">Sala libre</option>
                                <option value="2">Sala HIFT</option>
                                <option value="3">Personalizado</option>
                                <option value="4">Mixto</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="register-form__group" controlId="formBasicSessions">
                            <Form.Label>Sesiones por semana</Form.Label>
                            <Form.Control
                                type="tel"
                                inputMode="numeric"
                                placeholder="Sesiones semanales"
                                value={sessionsWeek}
                                onChange={handleSessionsChange}
                            />
                            <Form.Text className="text-muted">
                                Entre 1 y 7 sesiones permitidas.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="register-form__group" controlId="formBasicEmail">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Form.Group>

                        {/* <Form.Group controlId="formBasicFirstPayment">
                            <Form.Label>Rutina</Form.Label>
                            <Form.Control
                                type="text"
                                value={urlRoutine}
                                onChange={(e) => setRutine(e.target.value)}
                            />
                        </Form.Group> */}

                        <Form.Group className="register-form__group" controlId="formBasicDoc">
                            <Form.Label>Nro Documento</Form.Label>
                            <Form.Control
                                type="tel"
                                inputMode="numeric"
                                placeholder="Document"
                                value={documentNumber}
                                onChange={handleDocumentChange}
                            />
                            {documentStatus !== null && (
                                <p className="register-document-status" style={{ color: documentStatus ? "red" : "green" }}>
                                    {documentStatus ? "❌ Este documento ya está registrado." : "✅ El documento es nuevo."}
                                </p>
                            )}
                        </Form.Group>
                        <Form.Text className="text-muted register-document-status">Máximo 8 números permitidos.</Form.Text>

                        <Form.Group className="register-form__group" controlId="formBasicPhone">
                            <Form.Label>Teléfono</Form.Label>
                            <Form.Control
                                type="tel"
                                placeholder="Teléfono de contacto y WhatsApp"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="register-form__group" controlId="formBasicBirthdate">
                            <Form.Label>Fecha de Nacimiento</Form.Label>
                            <Form.Control
                                type="date"
                                value={birthDate}
                                onChange={(e) => setBirthdate(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="register-form__group" controlId="formBasicFirstPayment">
                            <Form.Label>Fecha de primera cuota</Form.Label>
                            <Form.Control
                                type="date"
                                value={firstPayment}
                                onChange={(e) => setFirstPayment(e.target.value)}
                            />
                        </Form.Group>

                        <fieldset className="register-form__health">
                            <legend className="float-none w-auto px-2 fs-6">Ficha de salud</legend>
                            <Form.Group controlId="medicalConditions">
                                <Form.Label>Condiciones médicas</Form.Label>
                                <Form.Control as="textarea" rows={2} name="medicalConditions" value={healthRecord.medicalConditions} onChange={handleHealthRecordChange} />
                            </Form.Group>
                            <Form.Group controlId="injuriesRestrictions" className="mt-2">
                                <Form.Label>Lesiones o restricciones</Form.Label>
                                <Form.Control as="textarea" rows={2} name="injuriesRestrictions" value={healthRecord.injuriesRestrictions} onChange={handleHealthRecordChange} />
                            </Form.Group>
                            <Form.Group controlId="allergies" className="mt-2">
                                <Form.Label>Alergias</Form.Label>
                                <Form.Control as="textarea" rows={2} name="allergies" value={healthRecord.allergies} onChange={handleHealthRecordChange} />
                            </Form.Group>
                            <Form.Group controlId="observations" className="mt-2">
                                <Form.Label>Observaciones</Form.Label>
                                <Form.Control as="textarea" rows={2} name="observations" value={healthRecord.observations} onChange={handleHealthRecordChange} />
                            </Form.Group>
                        </fieldset>

                        <Form.Group controlId="formBasicTyc" className="register-form__terms">
                            <Form.Check
                                type="checkbox"
                                label={
                                    <span>
                                        Acepta los Terminos y condiciones
                                    </span>
                                }
                                checked={tyc}
                                onChange={(e) => setTyc(e.target.checked)}
                                required
                            />
                        </Form.Group>
                        <Button
                            variant="primary"
                            type="submit"
                            className="register-form__submit"
                            disabled={loading || documentStatus === true || !tyc}
                        >
                            {loading ? <Spinner animation="border" size="sm" /> : "Registrar"}
                        </Button>

                    </Form>
                </Card.Body>
            </Card>
        </main>
    );
};

export default Register;
